import { useState, useEffect, FC, ReactNode } from 'react';
import { FireTVCampaignAutomationClient } from '@amzn/fire-tv-campaign-automation-service';
import { ClientContext } from './ClientContext';
import { useAuthentication } from './AuthenticationContext';
import { ProvisionedConstants } from 'src/constants';
import { ControlPlaneClientUtils } from 'src/utils/clients/controlPlane';
import { LoadingOverlay } from 'src/components/general/LoadingOverlay';
import { logger } from 'src/logger';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { ErrorDisplay } from 'src/components/general/ErrorDisplay';

interface ClientContextProviderProps {
  children: ReactNode;
}

const INITIALIZING = 'Initializing...';

/**
 * Provider component that manages the FireTV Campaign Automation client instance.
 *
 * This component:
 * 1. Initializes the control plane client when authentication is available
 * 2. Handles client cleanup when authentication changes
 * 3. Provides error handling for client initialization failures
 * 4. Makes the client instance available to child components via Context
 * 5. Properly cleans up async operations on unmount using AbortController
 *
 * @component
 * @example
 * // Wrap your app with the provider
 * <ClientContextProvider>
 *   <App />
 * </ClientContextProvider>
 */
export const ClientContextProvider: FC<ClientContextProviderProps> = ({ children }) => {
  // Internal state to manage the control plane client instance
  const [controlPlaneClient, setControlPlaneClient] = useState<FireTVCampaignAutomationClient>();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get authentication session from the Authentication Context
  const { authSession } = useAuthentication();

  /**
   * Effect to initialize or cleanup the client when authentication changes.
   * - Creates a new client instance when authentication is available
   * - Cleans up the client when authentication is lost
   * - Handles initialization errors
   * - Uses AbortController to prevent state updates after unmount
   */
  useEffect(() => {
    const abortController = new AbortController();

    const initializeClient = async () => {
      // Reset state when auth session is removed
      if (!authSession) {
        if (!abortController.signal.aborted) {
          setControlPlaneClient(undefined);
          setIsInitializing(false);
        }
        return;
      }

      if (!abortController.signal.aborted) {
        try {
          // Initialize the control plane client with authentication and configuration
          const client = await ControlPlaneClientUtils.getControlPlaneClient({
            authSession,
            crossAccountRoleArn: ProvisionedConstants.CONTROL_PLANE_CROSS_ACCOUNT_ROLE_ARN,
            region: ProvisionedConstants.CONTROL_PLANE_REGION,
            controlPlaneEndpoint: ProvisionedConstants.CONTROL_PLANE_ENDPOINT,
          });

          // Only update state if the component is still mounted
          if (!abortController.signal.aborted) {
            setControlPlaneClient(client);
          }
        } catch (error) {
          // Only update state and log error if the component is still mounted
          if (!abortController.signal.aborted) {
            logger.error('Failed to initialize FireTVCampaignAutomationServiceClient:', { error });
            setError(getErrorMessage(error));
            setControlPlaneClient(undefined);
          }
        } finally {
          if (!abortController.signal.aborted) {
            setIsInitializing(false);
          }
        }
      }
    };

    // Reset state and start initialization
    setIsInitializing(true);
    initializeClient();

    // Cleanup function to abort any pending operations when the component unmounts
    // or when authSession changes
    return () => abortController.abort();
  }, [authSession]); // Re-initialize when authentication changes

  if (error) {
    return <ErrorDisplay onNavigateHome={() => window.location.reload()} />;
  }

  if (isInitializing || !controlPlaneClient) {
    return <LoadingOverlay text={INITIALIZING} />;
  }

  return <ClientContext.Provider value={{ controlPlaneClient }}>{children}</ClientContext.Provider>;
};
